import { GridReadyEvent, RowSelectedEvent } from '@ag-grid-community/core';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import {
  AgFns,
  AvailableStatusDialogComponent,
  GridOptionsExt,
  IAvailableStatusDialogParams,
  IAvailableStatusDto,
  ISortModel,
  IStatusEnum,
  IStatusResult,
  ProductFns,
  ProxAgFns,
  Roles,
  RowSelectionMultipleCheckbox,
  StatusService,
  UtilDialogService,
  wrapBusy,
} from '@core';
import { DialogService } from 'primeng/dynamicdialog';
import { ProgramFrmComponent } from './program-frm.component';
import { ActiveStatusEnum, ProductTypeConfig, ProductTypeConfigAddon, ProductTypeConfigProduct, ProgramProductTypeConfig, ProgramTypeEnum } from '@models';
import { ProductTypeConfigFinderDialog } from './product-type-config.finder.dialog';
import { ChangeProductTypeConfigDialog } from './change-product-type-config.dialog';
import { OfferingPreviewDialogComponent } from './offering-preview.dialog';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { AccountDbSaveService } from '../services/account-db-save.service';
import { ToastrService } from 'ngx-toastr';
import { EntityFns } from '@data';
import * as _ from 'lodash';
import { ProgramProductTypePickerDialogComponent } from './program-product-type-picker.dialog.component';
import { UtilFns } from '@utils';
import { ConsolidateMeSummaryDialogComponent, IConsolidationAction } from './consolidate-me-summary.dialog.component';

@Component({
  selector: 'prox-program-product-type-configs-sub',
  templateUrl: './program-product-type-configs.sub.component.html',
  standalone: false,
})
export class ProgramProductTypeConfigsSubComponent implements OnInit, OnChanges, OnDestroy {
  @Input() visible!: boolean;
  @Input() parent!: ProgramFrmComponent;

  pptcGridOptions: GridOptionsExt = {};
  allPptcs: ProgramProductTypeConfig[] = [];
  pptcs: ProgramProductTypeConfig[] = [];
  selectedPptc?: ProgramProductTypeConfig;
  ptcpGridOptions: GridOptionsExt = {};
  ptcps: ProductTypeConfigProduct[] = [];
  ptcaGridOptions: GridOptionsExt = {};
  ptcas: ProductTypeConfigAddon[] = [];
  availableStatusDtos: IAvailableStatusDto[] = [];

  shouldShowOnlyProductsWithNoTags = false;

  // Consolidation feature. After Go Ahead, mutations are applied to in-memory Breeze entities;
  // visible feedback is the standard grid behavior (loser rows disappear, winner rows appear as new).
  // Undo / Save use the normal Undo / Save & Activate buttons.
  consolidationLoading = false;
  private consolidationRequestId = 0;

  constructor(
    public dbQueryService: AccountDbQueryService,
    public dbSaveService: AccountDbSaveService,
    public toastr: ToastrService,
    public dialogService: UtilDialogService,
    public pngDialogService: DialogService,
    public statusService: StatusService,
  ) {
    //
  }

  get program() {
    return this.parent.program;
  }

  ngOnInit() {
    this.pptcGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPptcGridReady,
      onRowSelected: this.onPptcRowSelected,

      rowModelType: 'clientSide',
      // params.data is ProgramProductTypeConfig
      getRowId: (params) => (params.data as ProgramProductTypeConfig).productTypeConfigId,
    });

    this.ptcpGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPtcpGridReady,

      rowModelType: 'clientSide',
      getRowId: (params) => (params.data as ProductTypeConfigProduct).productId,
    });

    this.ptcaGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPtcaGridReady,

      rowModelType: 'clientSide',
      getRowId: (params) => (params.data as ProductTypeConfigAddon).pricedAddonId,
    });

    this.refresh();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Cancel any in-progress consolidation when navigating away from this tab
    if (changes['visible'] && !this.visible) {
      this.consolidationRequestId++;
      this.consolidationLoading = false;
    }
    this.refresh();
  }

  ngOnDestroy(): void {
    // Cancel any in-progress consolidation by invalidating the request ID
    this.consolidationRequestId++;
  }

  @wrapBusy
  async refresh(forceRefresh = false) {
    if (!this.visible && !forceRefresh) return;
    this.parent.updateComponentNameMap(this);

    this.allPptcs = this.program.programProductTypeConfigs.slice();
    this.refreshPptcs();
    // needed to refresh editability
    this.onPptcGridReady();
    await AgFns.refreshGrid(this.pptcGridOptions, this.pptcs);
  }

  onPptcGridReady(event?: GridReadyEvent) {
    // ProgramProductTypeConfig
    const prodTypePath = 'productTypeConfig.pricedProductType.productType';
    const colDefs = [
      { ...ProxAgFns.getSupplierColDef(prodTypePath + '.supplier.name') },
      { ...ProxAgFns.getManufacturerColDef(prodTypePath + '.manufacturer.name') },
      ...(this.parent.authUser?.hasOrigRole(Roles.GlobalAdmin)
        ? [{ headerName: 'Product Key', field: prodTypePath + '.productKey', ...AgFns.flexWidth(120) }]
        : []),
      {
        ...ProxAgFns.getProductTypeConfigColDef('productTypeConfig.name'),
        cellRenderer: (params) => {
          const pptc = params.data as ProgramProductTypeConfig;
          if (!pptc || this.parent.isReadOnly()) {
            return pptc?.productTypeConfig?.name || '';
          }
          return `<span style="color: #3b82f6; text-decoration: underline; cursor: pointer;">${pptc.productTypeConfig.name}</span>`;
        },
        onCellClicked: (params) => {
          if (!this.parent.isReadOnly()) {
            this.onChangeConfig(params.data as ProgramProductTypeConfig);
          }
        },
      },
      {
        // PROOF OF CONCEPT - supplier images and color swatches. See offering-preview.dialog.ts.
        ...AgFns.createButtonProps('', this.onShowOffering.bind(this), { label: 'Show Offering' }),
        sortable: false,
        ...AgFns.fixWidth(140),
      },
      {
        // The status of the whole line, not only the configuration: Inactive or Phaseout on any part
        // (style, product, choice, feature) shows here, as on the Available? icon.
        headerName: 'Status',
        colId: 'availableStatus',
        valueGetter: (p) => this.statusService.getAvailableStatusName(p.data),
        cellClass: (p) => this.statusService.getAvailableStatusClass(p.data),
        filter: 'agTextColumnFilter',
        ...AgFns.fixWidth(100),
      },
      { ...ProxAgFns.getProductNameColDef(prodTypePath + '.name') },

      {
        ...AgFns.createIconProps('Available?', '', this.showAvailableStatus.bind(this), {
          label: '',
          getIcon: this.getIcon.bind(this),
        }),
        cellStyle: { 'justify-content': 'center', display: 'flex' },
        sortable: false,

        ...AgFns.fixWidth(100),
      },
      {
        ...AgFns.createCheckboxProps('Discontinued?', 'isDiscontinued'),
        editable: () => !this.parent.isReadOnly(),
        suppressHeaderMenuButton: true,
        ...AgFns.fixWidth(120),
      },
      ...(this.program.programTypeId !== ProgramTypeEnum.Inventory
        ? [
            {
              ...AgFns.createCheckboxProps('Force Approval?', 'isForceApproval'),
              editable: () => !this.parent.isReadOnly(),
              ...AgFns.fixWidth(110),
            },
          ]
        : []),
      {
        headerName: 'Tags',
        valueGetter: (p) => {
          const pptc = p.data as ProgramProductTypeConfig;
          if (pptc.entityAspect.entityState.isDeleted() || pptc.entityAspect.entityState.isDetached()) {
            return '';
          }
          return pptc.productTypeConfig.pricedProductType.programProductTypeTagsDescr;
        },
        ...AgFns.flexWidth(100),
      },
      {
        headerName: 'Base Price',
        field: 'productTypeConfig.pricedProductType.basePrice',
        ...AgFns.fixWidth('money'),
      },
      {
        headerName: 'Inventory Price Override',
        field: 'productTypeConfig.pricedProductType.accountOwnedBasePrice',
        ...AgFns.fixWidth('money'),
      },
      {
        headerName: 'Restock Fee',
        field: 'productTypeConfig.pricedProductType.restockPrice',
        ...AgFns.fixWidth('money'),
      },
      ...(this.program.programTypeId === ProgramTypeEnum.Inventory
        ? [
            {
              headerName: 'Reissue Price',
              field: 'productTypeConfig.pricedProductType.reissuancePrice',
              ...AgFns.fixWidth('money'),
            },
          ]
        : []),

      this.parent.ifEditable(
        ProxAgFns.getEntityDeleteColDef(this.onPptcDelete.bind(this), { calcDisabled: () => this.parent.isReadOnly() }),
      ),
    ];
    const sortModel: ISortModel = [
      { colId: 'productTypeConfig.name', sort: 'asc' },
    ];

    AgFns.initGrid(event ?? this.pptcGridOptions, colDefs, sortModel);
  }

  async showAvailableStatus(entity: ProgramProductTypeConfig | ProductTypeConfigProduct) {
    let headerName = entity instanceof ProgramProductTypeConfig ? 'Product Type' : 'Product';
    await AvailableStatusDialogComponent.open(
      this.pngDialogService,
      {
        entity: entity,
        subjectTypeName: 'Priced Product Type',
      },
      {
        header: headerName + ' Status Detail',
      },
    );
  }

  onAvailableClick(event) {}

  getIcon(entity: ProgramProductTypeConfig): string {
    return this.statusService.getStatusIcon(entity);
  }

  refreshPptcs() {
    if (this.shouldShowOnlyProductsWithNoTags) {
      this.pptcs = this.allPptcs.filter((x) => x.productTypeConfig.pricedProductType.programProductTypeTagsDescr == '');
    } else {
      this.pptcs = this.allPptcs;
    }
  }

  /* The Features grid shows every row of the selected configuration.
   *
   * It filtered out unavailable rows while the Program was Active (2026-07-22), to keep X's
   * out of an at-a-glance scan. That test rolls up the Account and the Program, and an
   * Account on Hold makes every row unavailable - so the grid emptied itself on PGCPS-Warehouse
   * and said only "14 rows are hidden".
   *
   * Hold is also the state you must be in to edit, so the filter removed the products exactly
   * when someone needed to work on them. A product blocked further down - Inactive in the
   * supplier catalog - is a different matter and is still worth marking, but not by hiding it.
   */

  async onTags(pptc: ProgramProductTypeConfig) {
    await this.dialogService.createFinder(ProgramProductTypePickerDialogComponent, {
      tags: this.parent.pptts,
      parents: [pptc.productTypeConfig.pricedProductType],
      rowSelection: RowSelectionMultipleCheckbox,
    });
  }

  calcTagsLabel(pptc: ProgramProductTypeConfig) {
    // This can happen during deletion.
    if (pptc.entityAspect.entityState.isDeleted()) {
      return '';
    }

    return pptc.productTypeConfig.pricedProductType.programProductTypeTagsDescr == ''
      ? ' - NONE - '
      : pptc.productTypeConfig.pricedProductType.programProductTypeTagsDescr;
  }

  onPtcpGridReady(event: GridReadyEvent) {
    // ProductTypeConfigProducts
    const colDefs = [
      // { headerName: 'SKU', field: 'product.productKey', filter: 'agSetColumnFilter', maxWidth: 200 },
      {
        colId: 'displayOrder',
        hide: true,
        valueGetter: (params) => {
          const ptcp = params.data as ProductTypeConfigProduct;
          return ptcp?.product?.getSortOrder() ?? 0;
        },
      },
      ProductFns.getAllFeaturesColDef((x) => {
        return x.data.product;
      }),
      {
        headerName: '(+) Base Price',
        colId: 'ttlFeature.price',
        editable: false,
        sortable: false,
        valueGetter: (params) => {
          return ProductFns.getProductTypeConfigPriceInfo(params.data).totalFeaturesPrice;
        },
        ...AgFns.fixWidth('money'),
      },
      {
        headerName: '(+) Inventory Price',
        colId: 'ttlFeature.inventoryPrice',
        editable: false,
        sortable: false,
        valueGetter: (params) => {
          const priceInfo = ProductFns.getProductTypeConfigPriceInfo(params.data);
          return priceInfo.pricedProductTypeFeatureChoices.reduce(
            (sum, pptfc) => sum + (pptfc.accountOwnedPrice != null ? pptfc.accountOwnedPrice : pptfc.price),
            0,
          );
        },
        ...AgFns.fixWidth('money'),
      },
      {
        ...AgFns.createIconProps('Available?', '', this.showAvailableStatus.bind(this), {
          label: '',
          getIcon: this.getIcon.bind(this),
        }),
        cellStyle: { 'justify-content': 'center', display: 'flex' },
        sortable: false,
        ...AgFns.fixWidth(100),
      },
    ];
    const sortModel: ISortModel = [{ colId: 'displayOrder', sort: 'asc' }];
    AgFns.initGrid(event, colDefs, sortModel);
  }

  onPtcaGridReady(event: GridReadyEvent) {
    // ProductTypeConfigAddons
    const colDefs = [
      { headerName: 'Add-On', field: 'pricedAddon.name', ...AgFns.flexWidth(100) },
      {
        ...AgFns.createIconProps("Add'l Info?", '', this.showInfoStatus.bind(this), {
          label: '',
          getIcon: this.getInfoIcon.bind(this),
        }),
        cellStyle: { 'justify-content': 'center', display: 'flex' },
        sortable: false,
        ...AgFns.fixWidth(100),
      },
      { headerName: 'Info Question', field: 'pricedAddon.addon.additionalInfoDescription', ...AgFns.flexWidth(100) },
      { headerName: '(+) Base Price)', field: 'pricedAddon.price', ...AgFns.fixWidth('money') },
      { headerName: '(+) Inventory Price', field: 'pricedAddon.accountOwnedPrice', ...AgFns.fixWidth('money') },
    ];
    const sortModel: ISortModel = [{ colId: 'pricedAddon.name', sort: 'asc' }];
    AgFns.initGrid(event, colDefs, sortModel);
  }

  showInfoStatus() {}

  getInfoIcon(row: ProductTypeConfigAddon): string {
    if (row.pricedAddon.addon.needsAdditionalInfo) {
      return <string>'fa-sharp fa-solid fa-check';
    }
    return '';
  }

  async onPptcRowSelected(event: RowSelectedEvent) {
    if (!event.node.isSelected()) return;
    const pptc = event.data as ProgramProductTypeConfig;
    this.selectedPptc = pptc;
    if (pptc == null) return;

    // get features and add-ons for the selected config
    const ptc = await this.dbQueryService.getProductTypeConfig(pptc.productTypeConfigId);

    this.ptcps = ptc?.productTypeConfigProducts ?? [];
    this.ptcas = ptc?.productTypeConfigAddons ?? [];
  }

  /// ------------------------------------------------------------------

  @wrapBusy
  async onPptcAdd() {
    if (this.allPptcs == null) return;
    const ptcSet = new Set(this.allPptcs.map((pg) => pg.productTypeConfig));
    const foundPtcs = await this.dialogService.createFinder(ProductTypeConfigFinderDialog, {
      header: 'Select Products',
      accountId: this.program.accountId,
      programTypeId: this.program.programTypeId,
      rowSelection: RowSelectionMultipleCheckbox,
    });
    const newPtcs = foundPtcs.filter((x) => !ptcSet.has(x));
    if (newPtcs.length != foundPtcs.length) {
      this.toastr.warning('Some Products were not added because they have already been added previously.', 'Already added');
    }

    const newPptcs = newPtcs.map((x) => {
      return this.dbSaveService.createEntity(ProgramProductTypeConfig, {
        programId: this.program.id,
        productTypeConfigId: x.id,
      });
    });
    this.allPptcs?.push(...newPptcs);
    AgFns.refreshGrid(this.pptcGridOptions, this.pptcs);
    // anytime the productTypeConfigs change the paFeatures change - but they don't change any other time
    // this.parent.calcPaFeatures();
  }

  @wrapBusy
  /**
   * PROOF OF CONCEPT. Opens the offering the way a buyer meets it - color chips, sizes that
   * reflect what each color is actually offered in, and a main image that follows the color.
   * Color and size are live data; only the supplier image URLs are stubbed.
   */
  async onShowOffering(pptc: ProgramProductTypeConfig) {
    const pt = pptc.productTypeConfig?.pricedProductType?.productType;
    await OfferingPreviewDialogComponent.openDialog(this.pngDialogService, {
      productTypeConfigId: pptc.productTypeConfigId,
      configName: pptc.productTypeConfig?.name ?? '',
      productTypeName: pt?.name ?? '',
      productKey: pt?.productKey ?? null,
    });
  }

  async onChangeConfig(pptc: ProgramProductTypeConfig) {
    // Check for unsaved changes first
    if (this.dbSaveService.hasChanges()) {
      this.toastr.warning('Please save or cancel your changes before changing configurations.', 'Unsaved Changes');
      return;
    }

    // Get the current product type (we can only change to another config of the same product type)
    const currentPricedProductType = pptc.productTypeConfig.pricedProductType;

    // Open the change configuration dialog
    const foundPtcs = await this.dialogService.createFinder(ChangeProductTypeConfigDialog, {
      header: `Change Configuration for ${currentPricedProductType.productType.name}`,
      pricedProductTypeId: currentPricedProductType.id,
      currentConfigId: pptc.productTypeConfigId,
      rowSelection: 'single',
    });

    if (foundPtcs && foundPtcs.length > 0) {
      const newPtc = foundPtcs[0];

      // Check if the new config is different from the current one
      if (newPtc.id === pptc.productTypeConfigId) {
        this.toastr.info('The selected configuration is already in use.', 'No Change');
        return;
      }

      // Check if this config is already in the program
      const alreadyExists = this.allPptcs.some((p) => p.productTypeConfigId === newPtc.id);
      if (alreadyExists) {
        this.toastr.warning('This configuration is already in the program.', 'Cannot Change');
        return;
      }

      // Instead of modifying the FK directly, delete the old entity and create a new one
      // This avoids Breeze conflict detection issues
      const programId = pptc.programId;
      const oldIsDiscontinued = pptc.isDiscontinued;
      const oldIsForceApproval = pptc.isForceApproval;

      // Delete the old ProgramProductTypeConfig
      EntityFns.deleteOrDetach(pptc.entityAspect);

      // Create a new ProgramProductTypeConfig with the new configuration
      const newPptc = this.dbSaveService.createEntity(ProgramProductTypeConfig, {
        programId: programId,
        productTypeConfigId: newPtc.id,
        isDiscontinued: oldIsDiscontinued,
        isForceApproval: oldIsForceApproval,
      });

      // Add to the local array
      const index = this.allPptcs.indexOf(pptc);
      if (index !== -1) {
        this.allPptcs.splice(index, 1, newPptc);
      }

      // Save the change immediately
      try {
        await this.dbSaveService.saveChanges();
        this.toastr.success(`Configuration changed to ${newPtc.name}`, 'Configuration Updated');

        // Refresh to reload navigation properties
        try {
          await this.refresh(true);
        } catch (refreshError) {
          console.error('Error during refresh after successful save:', refreshError);
          // Don't show error toast for refresh issues - the save was successful
        }
      } catch (saveError) {
        console.error('Error saving configuration change:', saveError);
        this.toastr.error('Failed to save configuration change', 'Save Error');
        // Refresh to revert the change
        try {
          await this.refresh(true);
        } catch (refreshError) {
          console.error('Error during refresh after failed save:', refreshError);
        }
      }
    }
  }

  @wrapBusy
  async onPptcDelete(pptc: ProgramProductTypeConfig) {
    if (!this.allPptcs) return;
    // TODO: need to detach all of this ProductTypeConfigs tied ProgramViews, ProgramRapidTemplates

    // detach all of this ProductTypeConfig tied to ProgramViews, ProgramRapidTemplates
    const promises = [this.dbQueryService.getProgramViews(this.program.id), this.dbQueryService.getRapidOrders(this.program.id)];
    await Promise.all(promises);

    this.program.entityAspect.markNavigationPropertyAsLoaded('programViews');
    this.program.entityAspect.markNavigationPropertyAsLoaded('programRapidTemplates');

    const ptcId = pptc.productTypeConfigId;

    const pvMaps = _.flatMap(this.program.programViews, (pv) =>
      pv.programViewProductTypeConfigs.filter((x) => x.productTypeConfigId == ptcId),
    );
    const prtMaps = _.flatMap(this.program.programRapidTemplates, (pr) =>
      pr.programRapidTemplateProductTypeConfigs.filter((x) => x.productTypeConfigId == ptcId),
    );
    [...pvMaps, ...prtMaps].forEach((map) => {
      EntityFns.deleteOrDetach(map.entityAspect);
    });
    EntityFns.deleteOrDetach(pptc.entityAspect);
    _.remove(this.allPptcs, pptc);
    // const x = this.pptcs.filter((x) => x.productTypeConfig.pricedProductType == null);
    this.refreshPptcs();
    await AgFns.refreshGrid(this.pptcGridOptions, this.pptcs);
  }

  // ---- Consolidation Feature ----
  //
  // Precondition: supplier priced configs have been curated so each identity group has exactly
  // one Active winner and the rest Inactive.
  //
  // Scan: PPTCs in this program whose ProductTypeConfig.activeStatusId === Inactive (losers).
  // For each loser, find the unique Active sibling ProductTypeConfig under the same PricedProductType (winner).
  //
  // Case A: winner already attached in this program → delete loser only.
  // Case B: winner not attached → attach winner (carry over isForceApproval), then delete loser.
  //         Tags come along automatically — they live on PricedProductType, shared by winner + loser.
  // Case C: no unambiguous winner (0 or 2+ Active peers) → leave loser alone.
  //
  // All changes are applied to in-memory Breeze entities only. Persist via Save / Save & Activate.

  @wrapBusy
  async consolidateMe() {
    const thisRequestId = ++this.consolidationRequestId;
    this.consolidationLoading = true;

    try {
      if (!this.allPptcs || this.allPptcs.length === 0) {
        this.toastr.warning('No products to analyze.');
        return;
      }

      const losers = this.allPptcs.filter(
        (pptc) =>
          pptc.productTypeConfig?.activeStatusId === ActiveStatusEnum.Inactive &&
          !pptc.entityAspect.entityState.isDeleted() &&
          !pptc.entityAspect.entityState.isDetached(),
      );

      if (losers.length === 0) {
        this.toastr.info('No inactive product configurations found in this program. Nothing to consolidate.');
        return;
      }

      const uniquePptIds = Array.from(new Set(losers.map((l) => l.productTypeConfig.pricedProductTypeId)));
      const siblings = await this.dbQueryService.getProductTypeConfigsForPricedProductTypes(uniquePptIds);

      if (thisRequestId !== this.consolidationRequestId) return;

      // Identity fingerprint: same PPT + same set of PricedAddonIds + same set of active member ProductIds.
      // Two configs with different addon sets (e.g. one has an addon, the other doesn't) are
      // legitimately different offerings and must NOT be treated as winner/loser peers.
      const fingerprint = (ptc: ProductTypeConfig): string => {
        const addonIds = (ptc.productTypeConfigAddons ?? [])
          .map((a) => a.pricedAddonId)
          .sort()
          .join(',');
        const productIds = (ptc.productTypeConfigProducts ?? [])
          .filter((ptcp) => ptcp.product?.activeStatusId === ActiveStatusEnum.Active)
          .map((ptcp) => ptcp.productId)
          .sort()
          .join(',');
        return `${ptc.pricedProductTypeId}|A:${addonIds}|P:${productIds}`;
      };

      // Index the fully-expanded sibling PTCs by id so we can fingerprint using their loaded
      // addon/product collections (the loser's navigation off the PPTC may not have those loaded).
      const siblingById = new Map(siblings.map((s) => [s.id, s]));
      const siblingsByFingerprint = _.groupBy(siblings, fingerprint);
      const attachedPtcIds = new Set(this.allPptcs.map((p) => p.productTypeConfigId));

      const actions: IConsolidationAction[] = [];
      for (const loser of losers) {
        const loserPtc = loser.productTypeConfig;
        const productKey = loserPtc.pricedProductType?.productType?.productKey ?? '';
        const productTypeName = loserPtc.pricedProductType?.productType?.name ?? '';

        const loserExpanded = siblingById.get(loserPtc.id);
        if (!loserExpanded) {
          // Shouldn't happen — the loser's PPT is in the query set. Fall back to Case C.
          actions.push({
            loserConfigId: loserPtc.id,
            loserConfigName: loserPtc.name,
            productKey,
            productTypeName,
            case: 'C',
            reasonC: 'Internal error — could not load config details.',
            isForceApproval: loser.isForceApproval ?? false,
          });
          continue;
        }

        const loserFp = fingerprint(loserExpanded);
        const peers = (siblingsByFingerprint[loserFp] ?? []).filter((p) => p.id !== loserPtc.id);
        const activePeers = peers.filter((p) => p.activeStatusId === ActiveStatusEnum.Active);

        if (activePeers.length !== 1) {
          actions.push({
            loserConfigId: loserPtc.id,
            loserConfigName: loserPtc.name,
            productKey,
            productTypeName,
            case: 'C',
            reasonC:
              activePeers.length === 0
                ? 'No active peer exists — create the winner first.'
                : `Ambiguous — ${activePeers.length} active peers exist.`,
            isForceApproval: loser.isForceApproval ?? false,
          });
          continue;
        }

        const winner = activePeers[0];
        const winnerAlreadyAttached = attachedPtcIds.has(winner.id);

        actions.push({
          loserConfigId: loserPtc.id,
          loserConfigName: loserPtc.name,
          productKey,
          productTypeName,
          case: winnerAlreadyAttached ? 'A' : 'B',
          winnerConfigId: winner.id,
          winnerConfigName: winner.name,
          isForceApproval: loser.isForceApproval ?? false,
        });
      }

      if (thisRequestId !== this.consolidationRequestId) return;

      actions.sort((a, b) => a.productTypeName.localeCompare(b.productTypeName));

      const result = await ConsolidateMeSummaryDialogComponent.openDialog(this.pngDialogService, { actions });

      if (thisRequestId !== this.consolidationRequestId) return;
      if (!result?.confirmed) {
        this.toastr.info('Consolidation cancelled — no changes made.');
        return;
      }

      // Preload ProgramViews and ProgramRapidTemplates once — deleting a loser must also detach
      // its map rows in those tables (NO_ACTION FKs).
      await Promise.all([
        this.dbQueryService.getProgramViews(this.program.id),
        this.dbQueryService.getRapidOrders(this.program.id),
      ]);
      this.program.entityAspect.markNavigationPropertyAsLoaded('programViews');
      this.program.entityAspect.markNavigationPropertyAsLoaded('programRapidTemplates');

      const loserByPtcId = new Map(this.allPptcs.map((p) => [p.productTypeConfigId, p]));
      // Track winners attached during this pass so multiple losers pointing to the same winner
      // don't try to create a duplicate PPTC (Breeze rejects with a MergeStrategy error).
      // OR-merge isForceApproval across all losers that share a winner.
      const attachedInPass = new Map<string, ProgramProductTypeConfig>();
      let planned = 0;
      let blocked = 0;

      for (const action of actions) {
        if (action.case === 'C') {
          blocked++;
          continue;
        }

        const loserPptc = loserByPtcId.get(action.loserConfigId);
        if (!loserPptc) continue;

        if (action.case === 'B' && action.winnerConfigId) {
          const existing = attachedInPass.get(action.winnerConfigId);
          if (existing) {
            if (action.isForceApproval && !existing.isForceApproval) {
              existing.isForceApproval = true;
            }
          } else {
            const winnerPptc = this.dbSaveService.createEntity(ProgramProductTypeConfig, {
              programId: this.program.id,
              productTypeConfigId: action.winnerConfigId,
              isForceApproval: action.isForceApproval,
            });
            this.allPptcs.push(winnerPptc);
            attachedInPass.set(action.winnerConfigId, winnerPptc);
          }
        }

        this.deleteLoserAndMaps(loserPptc);
        planned++;
      }

      this.refreshPptcs();
      await AgFns.refreshGrid(this.pptcGridOptions, this.pptcs);

      if (planned > 0) {
        this.toastr.success(
          `Prepared ${planned} consolidation change(s). ${blocked > 0 ? blocked + ' blocked. ' : ''}Click Save or Save & Activate to commit — or Undo to revert.`,
        );
      } else if (blocked > 0) {
        this.toastr.warning(`${blocked} loser(s) could not be consolidated — no unambiguous winner.`);
      }
    } finally {
      this.consolidationLoading = false;
    }
  }

  /** Delete a loser PPTC and detach its dependent ProgramView / ProgramRapidTemplate map rows.
   *  Assumes ProgramViews and ProgramRapidTemplates are already loaded (caller preloads them). */
  private deleteLoserAndMaps(pptc: ProgramProductTypeConfig) {
    const ptcId = pptc.productTypeConfigId;
    const pvMaps = _.flatMap(this.program.programViews, (pv) =>
      pv.programViewProductTypeConfigs.filter((x) => x.productTypeConfigId == ptcId),
    );
    const prtMaps = _.flatMap(this.program.programRapidTemplates, (pr) =>
      pr.programRapidTemplateProductTypeConfigs.filter((x) => x.productTypeConfigId == ptcId),
    );
    [...pvMaps, ...prtMaps].forEach((map) => EntityFns.deleteOrDetach(map.entityAspect));

    EntityFns.deleteOrDetach(pptc.entityAspect);
    _.remove(this.allPptcs, pptc);
  }
}
